import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentsList = ({ documents, onDocumentUpload, onDocumentDelete }) => {
  const [isUploading, setIsUploading] = useState(false);

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'pdf': return 'FileText';
      case 'image': return 'Image';
      case 'video': return 'Video';
      case 'audio': return 'Mic';
      case 'iep': return 'Target';
      case 'assessment': return 'ClipboardCheck';
      default: return 'File';
    }
  };

  const getDocumentColor = (type) => {
    switch (type) {
      case 'pdf': return 'text-destructive';
      case 'image': return 'text-success';
      case 'video': return 'text-primary';
      case 'audio': return 'text-warning';
      case 'iep': return 'text-accent';
      case 'assessment': return 'text-secondary';
      default: return 'text-muted-foreground';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const handleFileUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setIsUploading(true);
    
    // Simulate upload process
    setTimeout(() => {
      const newDocument = {
        id: Date.now(),
        name: file?.name,
        type: file?.type?.includes('pdf') ? 'pdf' : 
              file?.type?.includes('image') ? 'image' : 
              file?.type?.includes('video') ? 'video' : 
              file?.type?.includes('audio') ? 'audio' : 'file',
        size: file?.size,
        uploadedBy: 'Sarah Johnson',
        uploadedAt: new Date()?.toISOString(),
        category: 'General'
      };
      
      onDocumentUpload?.(newDocument);
      setIsUploading(false);
    }, 2000);
  };

  const handleDownload = (document) => {
    console.log('Downloading document:', document?.name);
    // Simulate download
  };

  const handleDelete = (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      onDocumentDelete?.(documentId);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-card-foreground">Documents</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {documents?.length} files
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => document.getElementById('document-upload')?.click()}
            disabled={isUploading}
            iconName={isUploading ? 'Loader2' : 'Plus'}
            iconPosition="left"
            className={isUploading ? 'animate-spin' : ''}
          >
            {isUploading ? 'Uploading...' : 'Add'}
          </Button>
        </div>
      </div>
      <input
        id="document-upload"
        type="file"
        onChange={handleFileUpload}
        className="hidden"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.mp3,.wav"
      />
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {documents?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="FileText" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-2">No documents uploaded</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => document.getElementById('document-upload')?.click()}
              iconName="Upload"
              iconPosition="left"
            >
              Upload first document
            </Button>
          </div>
        ) : (
          documents?.map((doc) => (
            <div key={doc?.id} className="flex items-center gap-3 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors duration-200">
              <div className={`p-2 rounded ${getDocumentColor(doc?.type)} bg-current/10`}>
                <Icon 
                  name={getDocumentIcon(doc?.type)} 
                  size={16} 
                  className={getDocumentColor(doc?.type)} 
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {doc?.name}
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{formatFileSize(doc?.size)}</span>
                  <span>•</span>
                  <span>{new Date(doc.uploadedAt)?.toLocaleDateString()}</span>
                  {doc?.category && (
                    <>
                      <span>•</span>
                      <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-xs">
                        {doc?.category}
                      </span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDownload(doc)}
                  className="h-8 w-8"
                  title="Download"
                >
                  <Icon name="Download" size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(doc?.id)}
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  title="Delete"
                >
                  <Icon name="Trash2" size={14} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DocumentsList;